import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { object } from 'prop-types';
import React from 'react';
import styled, { withTheme } from 'styled-components';

import DataViewFilter from 'ravenjs/components/DataViewFilter';
import { STICKY_COLUMN_POSITION } from 'ravenjs/constants/list';
import TableCell from 'ravenjs/lib/TableCell';
import TableHead from 'ravenjs/lib/TableHead';
import TableRow from 'ravenjs/lib/TableRow';
import { callFunc, getHandler } from 'ravenjs/utils/actions';

import { DATA_LIST_VIEW_SCHEMA } from 'constants/proptypes';
import { addStickyOffsetInColumns, getSelectStickyProps } from 'utils/dataList';

const TableCellContent = styled.span``;

function _identity(value) {
    return function() {
        return value;
    };
}

function DataListHead(props) {
    const { actions, schema, theme } = props;
    const {
        refs = {},
        rows: rowProps = {},
        selected: selectedProps = {},
        sorting = {},
        dataViewFilters,
    } = schema;
    let { columns = [] } = schema;
    const { renderSelectableRef, cellProps, width: selectBoxWidth } = selectedProps;
    const renderSelectable = getHandler(actions, renderSelectableRef);
    const sortSchemaActive = !_isEmpty(sorting);
    const handleSortChange = getHandler(
        actions,
        sortSchemaActive && refs.onChangeSortRef,
        _identity
    );
    const hasSelectColumn = typeof renderSelectable === 'function';
    const selectColumnWidth = selectBoxWidth || '50px';
    const hasStickyColumnsAtLeft = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.LEFT)
    );
    const hasStickyColumnsAtRight = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.RIGHT)
    );

    function isHeadSortable(headCol) {
        return typeof headCol.sortable === 'boolean' ? headCol.sortable : true;
    }

    if (hasStickyColumnsAtLeft) {
        columns = addStickyOffsetInColumns(
            columns,
            STICKY_COLUMN_POSITION.LEFT,
            hasSelectColumn ? selectColumnWidth : '0px',
            theme
        );
    }

    if (hasStickyColumnsAtRight) {
        columns = addStickyOffsetInColumns(columns, STICKY_COLUMN_POSITION.RIGHT, '0px', theme);
    }

    const selectCellStickyProps = getSelectStickyProps(columns);

    return (
        <TableHead>
            <TableRow {...rowProps} zebra={false}>
                {hasSelectColumn && (
                    <TableCell
                        as="th"
                        cellProps={{
                            ...cellProps,
                            noPadding: true,
                        }}
                        col={selectCellStickyProps}
                        key="table_cell__selectable"
                        type="head"
                        align="center"
                        width={selectColumnWidth}
                    >
                        {callFunc(renderSelectable, 'head')}
                    </TableCell>
                )}
                {columns.map(headCol => {
                    const {
                        actions: headColActions,
                        cellProps: headColCellProps,
                        filter: filterProps,
                        ...restHeadCol
                    } = headCol;
                    const { align, title } = restHeadCol;
                    const sortableHead = sortSchemaActive && isHeadSortable(headCol);
                    const cellProps = {
                        ...headColCellProps,
                        cellType: sortableHead
                            ? 'sortable'
                            : _get(headCol, 'cellProps.cellType', ''),
                    };
                    const onClickCell =
                        sortableHead && headCol.id
                            ? () => handleSortChange(sorting.order, headCol.sortId || headCol.id)
                            : null;
                    const key = headCol.keyProp || `${headCol.id}_${headCol.title}`;
                    const idFilter = `${headCol.id}__filter`;
                    const filter =
                        filterProps && filterProps.options ? (
                            <DataViewFilter
                                handlers={actions}
                                options={filterProps.options}
                                id={idFilter}
                                type="column"
                                defaultValue={dataViewFilters ? dataViewFilters[idFilter] : []}
                            />
                        ) : (
                            ''
                        );
                    const infoText = _get(headCol, 'infoText', null);

                    const hideTooltip = _get(headCol, 'cellProps.hideHeadTooltip', false) === true;

                    return (
                        <TableCell
                            as="th"
                            actions={headColActions || []}
                            align={align}
                            cellProps={cellProps}
                            col={restHeadCol}
                            config={headCol}
                            handlers={actions}
                            hideTooltip={hideTooltip}
                            infoText={infoText}
                            key={key}
                            onSort={onClickCell}
                            sortSchema={sorting}
                            type="head"
                        >
                            <TableCellContent onClick={onClickCell}>{title}</TableCellContent>
                            {filter}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}

DataListHead.propTypes = {
    actions: object,
    schema: DATA_LIST_VIEW_SCHEMA,
    theme: object.isRequired,
};

DataListHead.defaultProps = {
    actions: {},
    schema: {},
};

export { DataListHead as DataListHeadUnwrapped };
export default withTheme(DataListHead);
